import { cn } from "@/lib/utils";

/**
 * Editorial table-of-contents card. Mirrors the TOC at the top of
 * DashboardArchitecturePage — a soft-tinted card holding a numbered
 * list of section titles, each linkable via fragment anchor.
 *
 * RTL-friendly via `text-start`. Numbers are Latin digits (site
 * convention).
 */
export type TableOfContentsItem = {
  /** Section title shown to the reader. */
  title: string;
  /** Anchor id matching the corresponding section header's id. */
  id: string;
};

export type TableOfContentsProps = {
  /** Card heading. Default: "תוכן עניינים" — rendered above the list. */
  heading?: string;
  items: TableOfContentsItem[];
  /** Extra classes on the wrapper. */
  className?: string;
};

export function TableOfContents({
  heading = "תוכן עניינים",
  items,
  className,
}: TableOfContentsProps) {
  if (items.length === 0) return null;

  return (
    <nav
      aria-label={heading}
      className={cn(
        "mb-12 rounded-2xl border border-stone-200 bg-stone-50/70",
        "px-6 py-5 sm:px-8 sm:py-6",
        className,
      )}
    >
      <p
        className={cn(
          "mb-3 text-[11px] font-semibold uppercase",
          "tracking-[0.18em] text-rose-600",
        )}
      >
        {heading}
      </p>

      <ol
        className={cn(
          "grid gap-x-6 gap-y-1",
          "sm:grid-cols-2",
          "text-[14px] text-stone-700",
        )}
      >
        {items.map((item, idx) => (
          <li key={item.id} className="flex items-baseline gap-2 py-1">
            <span className="shrink-0 font-mono text-rose-600 font-semibold">
              {String(idx + 1).padStart(2, "0")}
            </span>
            <a
              href={`#${item.id}`}
              className={cn(
                "min-w-0 truncate",
                "transition-colors hover:text-rose-700",
              )}
            >
              {item.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}
